import { eq } from "@-/com.npmjs.drizzle-orm";
import type { ActorTransaction } from "@-/dev.improve.haniverse.database.actor";
import {
  createOrReviveActorRow,
  type CreateOrReviveActorRowOutcome,
} from "@-/dev.improve.haniverse.database.actor/create-or-revive";
import {
  type ActorCardRowIds,
  deriveGraphemeMeaningCardRowIds,
  deriveGraphemePronunciationCardRowIds,
  deriveQuizCardRowIds,
  deriveWordSentenceCardRowIds,
} from "@-/dev.improve.haniverse.database.schemas.actor.identity";
import {
  actorSchema,
  type WordCardKind,
} from "@-/dev.improve.haniverse.database.schemas.actor.schema";

/**
 * What a card is about. A card is identified by its subject and by nothing else: its row ids are
 * derived from it, so every device creates the same rows for the same card.
 */
export type UserCardSubject =
  | { grapheme: string; kind: "meaning" }
  | { grapheme: string; kind: "pronunciation"; pinyin: string }
  | { kind: "word"; pinyin: string; word: string; wordCardKind: WordCardKind }
  /** A quiz card, by its quiz's catalogue UUID key. */
  | { key: string; kind: "quiz" };

export function deriveUserCardRowIds(subject: UserCardSubject): ActorCardRowIds {
  switch (subject.kind) {
    case "meaning": {
      return deriveGraphemeMeaningCardRowIds(subject.grapheme);
    }
    case "pronunciation": {
      return deriveGraphemePronunciationCardRowIds(subject);
    }
    case "word": {
      return deriveWordSentenceCardRowIds({
        kind: subject.wordCardKind,
        pinyin: subject.pinyin,
        word: subject.word,
      });
    }
    case "quiz": {
      return deriveQuizCardRowIds(subject.key);
    }
  }
}

type OverlayRow = { createdAt: string; deletedAt: string | null; id: string; userCardId: string };

const OVERLAY_ROW_COLUMNS = {
  createdAt: true,
  deletedAt: true,
  id: true,
  userCardId: true,
} as const;

/** Every overlay row of the subject, tombstones included. */
async function readOverlayRows(
  tx: ActorTransaction,
  subject: UserCardSubject,
): Promise<OverlayRow[]> {
  switch (subject.kind) {
    case "meaning": {
      return tx.queryRaw.userGraphemeMeaningCard.findMany({
        columns: OVERLAY_ROW_COLUMNS,
        where: { grapheme: subject.grapheme },
      });
    }
    case "pronunciation": {
      return tx.queryRaw.userGraphemePronunciationCard.findMany({
        columns: OVERLAY_ROW_COLUMNS,
        where: { grapheme: subject.grapheme, pinyin: subject.pinyin },
      });
    }
    case "word": {
      return tx.queryRaw.userWordSentenceCard.findMany({
        columns: OVERLAY_ROW_COLUMNS,
        where: { kind: subject.wordCardKind, pinyin: subject.pinyin, word: subject.word },
      });
    }
    case "quiz": {
      return tx.queryRaw.userQuizCard.findMany({
        columns: OVERLAY_ROW_COLUMNS,
        where: { key: subject.key },
      });
    }
  }
}

async function createOrReviveOverlayRow(
  tx: ActorTransaction,
  subject: UserCardSubject,
  ids: ActorCardRowIds,
  nowIso: string,
): Promise<CreateOrReviveActorRowOutcome> {
  const row = { createdAt: nowIso, id: ids.detailId, userCardId: ids.userCardId };
  switch (subject.kind) {
    case "meaning": {
      return createOrReviveActorRow(tx, actorSchema.userGraphemeMeaningCard, {
        ...row,
        grapheme: subject.grapheme,
      });
    }
    case "pronunciation": {
      return createOrReviveActorRow(tx, actorSchema.userGraphemePronunciationCard, {
        ...row,
        grapheme: subject.grapheme,
        pinyin: subject.pinyin,
      });
    }
    case "word": {
      return createOrReviveActorRow(tx, actorSchema.userWordSentenceCard, {
        ...row,
        kind: subject.wordCardKind,
        pinyin: subject.pinyin,
        word: subject.word,
      });
    }
    case "quiz": {
      return createOrReviveActorRow(tx, actorSchema.userQuizCard, { ...row, key: subject.key });
    }
  }
}

/**
 * The row ids of the subject's card. A card that already has an overlay row keeps the ids that row
 * uses, whatever they are: a live row first, then the row with the derived id, then the newest.
 * Rows written by this app always carry the derived ids, so the two agree; a row that came from
 * elsewhere (an old export, a hand-built fixture) is still the card, and is never duplicated.
 */
export async function resolveUserCardRowIds(
  tx: ActorTransaction,
  subject: UserCardSubject,
): Promise<ActorCardRowIds> {
  const derived = deriveUserCardRowIds(subject);
  const rows = await readOverlayRows(tx, subject);
  const [existing] = rows.sort(
    (a, b) =>
      Number(a.deletedAt !== null) - Number(b.deletedAt !== null) ||
      Number(a.id !== derived.detailId) - Number(b.id !== derived.detailId) ||
      b.createdAt.localeCompare(a.createdAt) ||
      a.id.localeCompare(b.id),
  );
  return existing === undefined
    ? derived
    : { ...derived, detailId: existing.id, userCardId: existing.userCardId };
}

/**
 * Make the subject's card exist: its `user_card` row and its overlay row, both live. A missing row
 * is created, a tombstoned one comes back as a new row, and a live one is left exactly as it is
 * (see `createOrReviveActorRow`).
 *
 * The card is created blank: not in study, not skipped, no mnemonic. Write whatever the caller
 * wants on it with an UPDATE afterwards, so that two devices creating the same card converge and
 * neither overwrites the other's.
 *
 * `userCard` says what happened to the `user_card` row.
 */
export async function ensureUserCardRows(
  tx: ActorTransaction,
  subject: UserCardSubject,
  nowIso: string,
): Promise<{ ids: ActorCardRowIds; userCard: CreateOrReviveActorRowOutcome }> {
  const ids = await resolveUserCardRowIds(tx, subject);
  // The overlay row references the card, so the card comes first.
  const userCard = await createOrReviveActorRow(tx, actorSchema.userCard, {
    createdAt: nowIso,
    id: ids.userCardId,
  });
  await createOrReviveOverlayRow(tx, subject, ids, nowIso);
  return { ids, userCard };
}

/**
 * Give a card a schedule that starts over: its `fsrs_card` row holds a new card due at `dueIso`.
 * A card has one schedule row for good, so this creates it, revives it or resets it. The caller
 * points `user_card.fsrs_card_id` at it afterwards.
 *
 * Call it only for a card that is not in study: it erases whatever the row held.
 */
export async function startUserCardSchedule(
  tx: ActorTransaction,
  {
    dueIso,
    fsrsCardId,
    nowIso,
    scheduledDays,
  }: { dueIso: string; fsrsCardId: string; nowIso: string; scheduledDays: number },
): Promise<void> {
  const newSchedule = {
    createdAt: nowIso,
    difficulty: 0,
    due: dueIso,
    elapsedDays: 0,
    lapses: 0,
    lastReview: null,
    learningSteps: 0,
    reps: 0,
    scheduledDays,
    stability: 0,
    state: 0,
    updatedAt: null,
  };
  const outcome = await createOrReviveActorRow(tx, actorSchema.fsrsCard, {
    ...newSchedule,
    id: fsrsCardId,
  });
  if (outcome === "kept") {
    // A live schedule the card was not using: the card left study without its schedule being
    // retired (a removed word card, or two devices' changes merged by sync).
    await tx
      .update(actorSchema.fsrsCard)
      .set(newSchedule)
      .where(eq(actorSchema.fsrsCard.id, fsrsCardId));
  }
}
